/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Sparkles, Loader2, Trash2, Wand2, Download, Play, Pause,
  AlertCircle, Captions, Volume2, ExternalLink,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { oliviaAPI } from '../lib/api';

const ASPECT_LABELS = {
  '9:16': 'Vertical (TikTok / Reels)',
  '1:1':  'Square (Instagram feed)',
  '16:9': 'Widescreen (YouTube)',
  '4:5':  'Portrait (Instagram)',
  '5:4':  'Landscape',
};

// Rough estimate: HeyGen's English TTS averages ~150 words per minute,
// or ~750 chars/min. Used purely as a UX hint, not enforced.
const estimateSeconds = (script) => {
  const chars = (script || '').trim().length;
  if (!chars) return 0;
  return Math.max(2, Math.round((chars / 750) * 60));
};

function VoiceCard({ voice, selected, onSelect, audioRef }) {
  const [playing, setPlaying] = useState(false);

  const toggle = (e) => {
    e.stopPropagation();
    const el = audioRef.current;
    if (!el) return;
    if (playing) { el.pause(); setPlaying(false); return; }
    // Pause any other voice's audio.
    document.querySelectorAll('audio[data-olivia-voice]').forEach((a) => {
      if (a !== el) { a.pause(); }
    });
    el.currentTime = 0;
    el.play();
    setPlaying(true);
  };

  return (
    <button
      type="button"
      onClick={() => onSelect(voice.voice_id)}
      data-testid={`olivia-voice-card-${voice.voice_id}`}
      className={`group text-left rounded-lg border-2 p-4 transition-all ${
        selected
          ? 'border-violet-500 bg-violet-50/60 shadow-sm'
          : 'border-zinc-200 hover:border-violet-300 bg-white'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-medium text-sm text-zinc-900">{voice.name?.trim() || 'Olivia'}</div>
          <div className="mt-0.5 flex items-center gap-1.5 flex-wrap">
            {voice.language && (
              <Badge variant="secondary" className="text-[10px] py-0 px-1.5 font-normal">
                {voice.language}
              </Badge>
            )}
            <Badge variant="outline" className="text-[10px] py-0 px-1.5 font-normal capitalize">
              {voice.gender}
            </Badge>
          </div>
          <div className="mt-2 text-[10px] text-zinc-400 font-mono break-all">
            {voice.voice_id.slice(0, 16)}…
          </div>
        </div>
        {voice.preview_audio && (
          <button
            type="button"
            onClick={toggle}
            className={`rounded-full w-9 h-9 flex items-center justify-center transition-colors ${
              playing ? 'bg-violet-600 text-white' : 'bg-zinc-100 text-zinc-700 hover:bg-violet-100'
            }`}
            data-testid={`olivia-voice-preview-${voice.voice_id}`}
            aria-label={playing ? 'Pause preview' : 'Play preview'}
          >
            {playing ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}
          </button>
        )}
      </div>
      {voice.preview_audio && (
        <audio
          ref={audioRef}
          data-olivia-voice={voice.voice_id}
          src={voice.preview_audio}
          onEnded={() => setPlaying(false)}
          onPause={() => setPlaying(false)}
          preload="none"
        />
      )}
    </button>
  );
}

export default function OliviaPage() {
  const [avatar, setAvatar] = useState(null);
  const [voiceId, setVoiceId] = useState('');
  const [aspectRatio, setAspectRatio] = useState('9:16');
  const [caption, setCaption] = useState(false);
  const [title, setTitle] = useState('');
  const [script, setScript] = useState('');
  const [generating, setGenerating] = useState(false);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // One audio ref per voice card.
  const audio0 = useRef(null);
  const audio1 = useRef(null);
  const audio2 = useRef(null);
  const audioRefs = [audio0, audio1, audio2];

  const loadAvatar = useCallback(async () => {
    try {
      const res = await oliviaAPI.getAvatar();
      setAvatar(res.data);
      if (res.data?.voices?.[0]?.voice_id && !voiceId) {
        setVoiceId(res.data.voices[0].voice_id);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load Olivia');
    }
  }, [voiceId]);

  const loadHistory = useCallback(async () => {
    try {
      const res = await oliviaAPI.list();
      setHistory(res.data.videos || []);
    } catch (err) {
      console.error('history load failed', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAvatar(); loadHistory(); }, [loadAvatar, loadHistory]);

  // Auto-poll while any video is still rendering (HeyGen takes 60-180s
  // for a short clip — the UI flips to "ready" without a manual refresh).
  useEffect(() => {
    const hasPending = history.some((h) => h.status === 'pending');
    if (!hasPending) return undefined;
    const id = setInterval(() => { loadHistory(); }, 6000);
    return () => clearInterval(id);
  }, [history, loadHistory]);

  const handleGenerate = async () => {
    if (!script.trim()) {
      toast.error('Please enter a script');
      return;
    }
    if (!voiceId) {
      toast.error('Please choose a voice');
      return;
    }
    setGenerating(true);
    try {
      await oliviaAPI.create({
        script: script.trim(),
        voice_id: voiceId,
        aspect_ratio: aspectRatio,
        caption,
        title: title.trim() || undefined,
      });
      toast.success('Olivia is rendering — typically takes 60-180 seconds');
      setScript('');
      setTitle('');
      await loadHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this Olivia video? You can re-render from the same script.')) return;
    try {
      await oliviaAPI.delete(id);
      setHistory((prev) => prev.filter((h) => h.id !== id));
      toast.success('Deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const estSeconds = useMemo(() => estimateSeconds(script), [script]);
  const maxChars = avatar?.max_script_chars || 1500;
  const charsLeft = maxChars - (script?.length || 0);

  return (
    <div className="max-w-7xl mx-auto space-y-8" data-testid="olivia-page">
      <header className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-violet-50 text-violet-600 flex items-center justify-center">
            <Sparkles size={20} />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-zinc-900 tracking-tight">Olivia</h1>
            <p className="text-sm text-zinc-500">Give Olivia a script — HeyGen renders her speaking it in lip-synced video.</p>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left: composer */}
        <div className="lg:col-span-3 space-y-5">
          <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-4">
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">Title (optional)</label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Story Drop — New Bridal Collection Teaser"
                data-testid="olivia-title-input"
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">Script</label>
                <span className={`text-[10px] ${charsLeft < 100 ? 'text-amber-600' : 'text-zinc-400'}`}>
                  {script.length} / {maxChars} chars · ~{estSeconds}s read time
                </span>
              </div>
              <Textarea
                rows={7}
                value={script}
                onChange={(e) => setScript(e.target.value.slice(0, maxChars))}
                placeholder="Hi, I'm Olivia from The Local Jewel. Today I'm showing you our newest oval lab-diamond engagement ring — handcrafted in 14k gold with a delicate hidden halo…"
                data-testid="olivia-script-input"
                className="font-normal"
              />
              <p className="text-[10px] text-zinc-400">
                Keep it conversational. Olivia delivers ~150 words per minute. For a 30-second clip, aim for 60-80 words.
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600 flex items-center gap-1.5">
                <Volume2 size={11} /> Voice — pick any of the 3 &quot;Olivia&quot; voices
              </label>
              {!avatar ? (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="h-[110px] rounded-lg bg-zinc-100 animate-pulse" />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {(avatar.voices || []).map((v, i) => (
                    <VoiceCard
                      key={v.voice_id}
                      voice={v}
                      selected={voiceId === v.voice_id}
                      onSelect={setVoiceId}
                      audioRef={audioRefs[i]}
                    />
                  ))}
                </div>
              )}
              <p className="text-[10px] text-zinc-400">
                Tap a voice card to select it. Use the play button to hear a 5-10 sec sample before committing.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">Aspect ratio</label>
                <div className="grid grid-cols-1 gap-1.5">
                  {(avatar?.aspect_ratios || ['9:16', '1:1', '16:9']).map((ar) => (
                    <button
                      key={ar}
                      type="button"
                      onClick={() => setAspectRatio(ar)}
                      data-testid={`olivia-aspect-${ar}`}
                      className={`text-left text-xs rounded-md border px-3 py-2 transition-colors ${
                        aspectRatio === ar
                          ? 'border-violet-500 bg-violet-50/60'
                          : 'border-zinc-200 hover:border-zinc-400'
                      }`}
                    >
                      <span className="font-mono font-semibold">{ar}</span>
                      <span className="ml-2 text-zinc-500">{ASPECT_LABELS[ar] || ar}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">Burned-in captions</label>
                <button
                  type="button"
                  onClick={() => setCaption((v) => !v)}
                  data-testid="olivia-caption-toggle"
                  className={`w-full text-left rounded-md border px-3 py-2 transition-colors ${
                    caption ? 'border-violet-500 bg-violet-50/60' : 'border-zinc-200 hover:border-zinc-400'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Captions size={14} className={caption ? 'text-violet-600' : 'text-zinc-400'} />
                      <span className="text-xs font-medium">{caption ? 'Captions ON' : 'No captions'}</span>
                    </div>
                    <div className={`w-8 h-4 rounded-full transition-colors flex items-center px-0.5 ${
                      caption ? 'bg-violet-600 justify-end' : 'bg-zinc-300 justify-start'
                    }`}>
                      <div className="w-3 h-3 rounded-full bg-white" />
                    </div>
                  </div>
                  <p className="text-[10px] text-zinc-500 mt-1.5">
                    {caption ? 'HeyGen burns subtitles into the MP4.' : 'Clean talking-head — no overlay text.'}
                  </p>
                </button>
              </div>
            </div>

            <div className="pt-2">
              <Button
                onClick={handleGenerate}
                disabled={generating || !script.trim() || !voiceId}
                className="w-full bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="olivia-generate-btn"
                size="lg"
              >
                {generating
                  ? <><Loader2 size={16} className="mr-2 animate-spin" /> Submitting to HeyGen…</>
                  : <><Wand2 size={16} className="mr-2" /> Generate Olivia video</>}
              </Button>
              <p className="text-[10px] text-zinc-400 text-center mt-2">
                Rendering runs in the background and typically takes 60-180 seconds for a 15-30s clip.
              </p>
            </div>
          </div>
        </div>

        {/* Right: Olivia preview + tips */}
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-3">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">Avatar</div>
            <div className="rounded-lg overflow-hidden bg-zinc-50 aspect-[9/16] flex items-center justify-center">
              {avatar?.preview_video_url ? (
                <video
                  src={avatar.preview_video_url}
                  poster={avatar.preview_image_url}
                  muted
                  loop
                  autoPlay
                  playsInline
                  className="w-full h-full object-cover"
                  data-testid="olivia-avatar-preview"
                />
              ) : avatar?.preview_image_url ? (
                <img
                  src={avatar.preview_image_url}
                  alt="Olivia"
                  className="w-full h-full object-cover"
                  data-testid="olivia-avatar-preview"
                />
              ) : (
                <div className="text-xs text-zinc-400">Loading preview…</div>
              )}
            </div>
            <div>
              <div className="font-medium text-sm text-zinc-900">Olivia</div>
              <div className="text-[11px] text-zinc-500">HeyGen Digital Twin · Avatar IV ready</div>
            </div>
          </div>

          <div className="bg-violet-50/60 border border-violet-200 rounded-xl p-4 text-xs text-violet-900 space-y-1.5">
            <div className="font-semibold flex items-center gap-1.5">
              <Sparkles size={13} /> Tips
            </div>
            <ul className="list-disc pl-4 space-y-1 text-[11px]">
              <li>Write like a person talks — short sentences, contractions, light pauses.</li>
              <li>Mention the product name or hook in the first 5 words for retention.</li>
              <li>End with a call to action (&quot;Tap to see it.&quot;, &quot;Link in bio.&quot;, &quot;DM us.&quot;).</li>
              <li>For a Pinterest Idea Pin, choose 9:16 + Captions ON.</li>
            </ul>
          </div>
        </div>
      </div>

      {/* History */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-zinc-900">Your Olivia videos</h2>
          <Button
            size="sm"
            variant="ghost"
            onClick={loadHistory}
            data-testid="olivia-refresh-history"
          >
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-64 rounded-xl bg-zinc-100 animate-pulse" />
            ))}
          </div>
        ) : history.length === 0 ? (
          <div className="bg-white border border-dashed border-zinc-300 rounded-xl p-10 text-center text-sm text-zinc-500" data-testid="olivia-empty-state">
            No videos yet. Write a script above and click <strong>Generate Olivia video</strong> to start.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {history.map((v) => (
              <OliviaCard key={v.id} v={v} onDelete={handleDelete} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function OliviaCard({ v, onDelete }) {
  const isReady = v.status === 'ready' && v.view_url;
  const isPending = v.status === 'pending';
  const isFailed = v.status === 'failed';

  return (
    <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden flex flex-col" data-testid={`olivia-card-${v.id}`}>
      <div className="relative bg-zinc-900 aspect-[9/16] flex items-center justify-center">
        {isReady ? (
          <video
            src={v.view_url}
            poster={v.thumbnail_url}
            controls
            playsInline
            className="w-full h-full object-cover"
            data-testid={`olivia-video-${v.id}`}
          />
        ) : isFailed ? (
          <div className="text-center p-6 text-rose-300">
            <AlertCircle size={28} className="mx-auto mb-2" />
            <div className="text-[11px]">{v.error || 'Generation failed'}</div>
          </div>
        ) : (
          <div className="text-center p-6 text-violet-200">
            <Loader2 size={28} className="mx-auto mb-2 animate-spin" />
            <div className="text-[11px]">Rendering with HeyGen…</div>
            <div className="text-[10px] text-violet-300/70 mt-1">Usually 60-180 seconds</div>
          </div>
        )}
        {v.duration && isReady && (
          <Badge className="absolute top-2 right-2 bg-black/60 text-white border-0 text-[10px]">
            {Math.round(v.duration)}s
          </Badge>
        )}
        <Badge className="absolute top-2 left-2 bg-black/60 text-white border-0 text-[10px] font-mono">
          {v.aspect_ratio}
        </Badge>
        {v.heygen_engine && (
          <Badge className="absolute bottom-2 left-2 bg-violet-600/80 text-white border-0 text-[10px] font-mono uppercase">
            {v.heygen_engine.replace('_', ' ')}
          </Badge>
        )}
      </div>

      <div className="p-3 space-y-2 flex-1 flex flex-col">
        <div className="text-xs text-zinc-700 line-clamp-2">
          {v.title || v.script || '—'}
        </div>
        <div className="text-[10px] text-zinc-400 mt-auto">
          {new Date(v.created_at).toLocaleString()}
          {isPending && <span className="ml-1.5 text-violet-600 font-medium">· pending</span>}
        </div>
        <div className="flex items-center gap-1.5 pt-1">
          {isReady && (
            <>
              <a
                href={v.download_url || v.view_url}
                download={`olivia-${v.id}.mp4`}
                className="flex-1 text-xs px-2.5 py-1.5 rounded-md bg-violet-600 hover:bg-violet-700 text-white inline-flex items-center justify-center gap-1.5"
                data-testid={`olivia-download-${v.id}`}
              >
                <Download size={12} /> Download MP4
              </a>
              <a
                href={v.view_url}
                target="_blank"
                rel="noreferrer"
                className="text-xs px-2 py-1.5 rounded-md border border-zinc-300 hover:bg-zinc-50 inline-flex items-center justify-center"
                title="Open in new tab"
                data-testid={`olivia-open-${v.id}`}
              >
                <ExternalLink size={12} />
              </a>
            </>
          )}
          <button
            type="button"
            onClick={() => onDelete(v.id)}
            className="text-xs px-2 py-1.5 rounded-md border border-zinc-300 hover:bg-rose-50 hover:border-rose-300 hover:text-rose-600 text-zinc-500 inline-flex items-center justify-center"
            title="Delete"
            data-testid={`olivia-delete-${v.id}`}
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}
